import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { LedgerApi } from '../../../../api/ledger';
import { CollapsibleTable } from './tableComponent/CollapsibleTable';
import './inventoryHistoryV2.css';
import { formatDateToIsoString } from '../../../../utils/dateUtils/ledgerDateUtils';

const ledgerApi = new LedgerApi();

// Add a function to process the data for combined columns
const transformTableData = (rows) => {
    return rows.map(row => {
        
        // Calculate the combined Negative Adjustments column (excluding systemAdjustmentUnits)
        const negativeAdjustments = (row.packedOutUnits || 0) + (row.adjustedDownUnits || 0);
        
        // Return a new row with the combined columns
        return {
            ...row,
            negativeAdjustments,
            negativeAdjustmentDetails: {
                packedOutUnits: row.packedOutUnits || 0,
                adjustedDownUnits: (row.adjustedDownUnits || 0) - (row.systemAdjustmentUnits || 0),
                systemAdjustmentUnits: row.systemAdjustmentUnits || 0
            }
        };
    });
};

const InventoryHistoryTableContainer = ({
    facilityId,
    groupBy = 'sku',
    activeFilters = [],
    setNotification,
    summarizePackout = false,
}) => {
    const [tableData, setTableData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [hasMore, setHasMore] = useState(true);
    const [page, setPage] = useState(0);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const scrollContainerRef = useRef(null);
    const syncedScrollRef = useRef(null);
    const prevFiltersRef = useRef(null);
    const prevGroupByRef = useRef(groupBy);
    const observerRef = useRef(null);

    // Intersection Observer callback for infinite scroll
    const handleObserver = useCallback(
        (entries) => {
            const target = entries[0];
            if (target.isIntersecting && !loading && !isLoadingMore && hasMore) {
                setPage((prev) => prev + 1);
            }
        },
        [loading, isLoadingMore, hasMore]
    );

    // Setup Intersection Observer
    useEffect(() => {
        const option = {
            root: null,
            rootMargin: '20px',
            threshold: 0,
        };

        observerRef.current = new IntersectionObserver(handleObserver, option);

        return () => {
            if (observerRef.current) observerRef.current.disconnect();
        };
    }, [handleObserver]);

    // Attach observer to the loader element
    useEffect(() => {
        const loaderElement = document.querySelector('#infinite-scroll-loader');
        if (loaderElement && observerRef.current) {
            observerRef.current.observe(loaderElement);
        }
    }, [tableData]);

    // Sync the horizontal scrolling between the table and the bottom scrollbar
    useEffect(() => {
        const contentContainer = scrollContainerRef.current;
        const syncedScrollContainer = syncedScrollRef.current;

        if (!contentContainer || !syncedScrollContainer) return;

        // Handle scroll from main content
        const handleContentScroll = () => {
            syncedScrollContainer.scrollLeft = contentContainer.scrollLeft;
        };

        // Handle scroll from bottom bar
        const handleSyncedScroll = () => {
            contentContainer.scrollLeft = syncedScrollContainer.scrollLeft;
        };

        contentContainer.addEventListener('scroll', handleContentScroll);
        syncedScrollContainer.addEventListener('scroll', handleSyncedScroll);

        return () => {
            contentContainer.removeEventListener('scroll', handleContentScroll);
            syncedScrollContainer.removeEventListener('scroll', handleSyncedScroll);
        };
    }, [tableData]);

    // Function to fetch adjustment reasons for modal
    const fetchAdjustmentReasons = async (itemId, groupByParam = groupBy) => {
        const queryString = filtersToApiParams(activeFilters);
        
        const cleanedQueryString = queryString
            .split('&')
            .filter(param => !param.startsWith('sku[]=') && !param.startsWith('lot[]='))
            .join('&');
        
        const response = await ledgerApi.getAdjustmentsAsReasons(groupByParam, itemId, facilityId, cleanedQueryString);

        if (!response.status) {
            return {
                status: false,
                data: { rows: {} },
                error: response.msg || 'Failed to fetch adjustment reasons'
            };
        }
        
        if (response.data && response.data.rows && 
            (response.data.rows.positive || response.data.rows.negative)) {
            return {
                status: true,
                data: response.data,
                error: null
            };
        } else {
            return {
                status: false,
                data: { rows: {} },
                error: 'Unexpected data format from API'
            };
        }
    };

    const getColumns = () => {
        if (groupBy === 'lot') {
            return [
                { id: 'lotId', label: 'Lot (Item Name)', toolTipLabel: 'Lot ID and associated item name' },
                { id: 'companySource', label: 'Source', toolTipLabel: 'Company source of item' },
                { id: 'startingUnits', label: 'Starting Inventory', toolTipLabel: 'Inventory level at date/time selected' },
                { id: 'receivedUnits', label: 'Receipts', toolTipLabel: 'Units received during the date range' },
                { id: 'negativeAdjustments', label: 'Negative Adjustments', toolTipLabel: 'Units packed out during the date range, Inventory adjustments that decreased unit count, System-generated adjustments to clear negative variance' },
                { id: 'adjustedUpUnits', label: 'Positive Adjustments', toolTipLabel: 'Inventory adjustments that increased unit count' },
                { id: 'endingUnitsWithoutAdjustments', label: 'Expected Ending Inventory', toolTipLabel: 'Ending inventory count without user adjustments' },
                { id: 'endingUnits', label: 'Actual Ending Inventory', toolTipLabel: 'Ending inventory count including user adjustments' },
                { id: 'difference', label: 'Inventory Variance', toolTipLabel: 'Difference between expected and actual inventory' },
                { id: 'unitsInMissingLocation', label: 'In Missing Location', toolTipLabel: 'Units left in the Missing Location at end of date range' },
            ];
        }

        return [
            { id: 'skuWithName', label: 'SKU (Item Name)', toolTipLabel: 'SKU and associated item name' },
            { id: 'companySource', label: 'Source', toolTipLabel: 'Company source of item' },
            { id: 'lotCount', label: 'Num of Lots', toolTipLabel: 'Number of lots for the SKU' },
            { id: 'startingUnits', label: 'Starting Inventory', toolTipLabel: 'Inventory level at date/time selected' },
            { id: 'receivedUnits', label: 'Receipts', toolTipLabel: 'Units received during the date range' },
            { id: 'negativeAdjustments', label: 'Negative Adjustments', toolTipLabel: 'Units packed out during the date range, Inventory adjustments that decreased unit count, System-generated adjustments to clear negative variance' },
            { id: 'adjustedUpUnits', label: 'Positive Adjustments', toolTipLabel: 'Inventory adjustments that increased unit count' },
            { id: 'negativePackUnits', label: 'Negative Pack', toolTipLabel: 'Negative Variance Units generated during the date range' },
            { id: 'endingUnitsWithoutAdjustments', label: 'Expected Ending Inventory', toolTipLabel: 'Ending inventory count without user adjustments' },
            { id: 'endingUnits', label: 'Actual Ending Inventory', toolTipLabel: 'Ending inventory count including user adjustments' },
            { id: 'difference', label: 'Inventory Variance', toolTipLabel: 'Difference between expected and actual inventory' },
            { id: 'unitsInMissingLocation', label: 'In Missing Location', toolTipLabel: 'Units left in the Missing Location at end of date range' },
        ];
    };

    const filtersToApiParams = (filters, summarizePackoutValue = summarizePackout) => {
        if (!filters.length) {
            return '';
        }

        const groupedParams = {};
        let dateParams = '';

        filters.forEach((filter) => {
            const { filterKey, data } = filter;

            if (filterKey === 'dateRange') {
                if (data && data.startDate && data.endDate) {
                    const startDate = new Date(data.startDate);
                    const endDate = new Date(data.endDate);
                    
                    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
                        return;
                    }

                    dateParams = `start=${formatDateToIsoString(startDate)}&end=${formatDateToIsoString(endDate)}`;
                }
                return;
            }

            const paramKey = filterKey;

            if (!groupedParams[paramKey]) {
                groupedParams[paramKey] = [];
            }

            if (Array.isArray(data)) {
                groupedParams[paramKey] = [...groupedParams[paramKey], ...data];
            } else if (data) {
                groupedParams[paramKey].push(data);
            }
        });

        const paramStrings = Object.entries(groupedParams).map(([key, values]) => {
            return values.map((value) => `${key}[]=${value}`).join('&');
        });

        const allParams = [dateParams, ...paramStrings, `summarizePackout=${summarizePackoutValue}`].filter(Boolean);

        return allParams.join('&');
    };

    const fetchTableData = async (isLoadingMore = false, forcePage = null) => {
        if (!isLoadingMore) {
            setLoading(true);
        } else {
            setIsLoadingMore(true);
        }

        const queryString = filtersToApiParams(activeFilters);
        const pageToUse = forcePage !== null ? forcePage : page;

        let response;

        if (groupBy === 'lot') {
            response = await ledgerApi.getGroupByLotData(facilityId, pageToUse, 50, queryString);
        } else {
            response = await ledgerApi.getGroupBySkuData(facilityId, pageToUse, 50, queryString);
        }

        if (response.status) {
            const newData = transformTableData(response.data.rows);
            setTableData((prev) => (isLoadingMore ? [...prev, ...newData] : newData));
            setHasMore(response.data.meta?.hasMore ?? false);
        } else {
            if (!isLoadingMore) {
                setTableData([]);
            }
            setNotification({ text: response.msg ?? 'Error fetching data', level: 'error' });
        }

        if (isLoadingMore) {
            setIsLoadingMore(false);
        } else {
            setLoading(false);
        }
    };

    const hasGroupByChanged = () => {
        return prevGroupByRef.current !== groupBy;
    };

    // Effect to handle filter changes
    useEffect(() => {
        const groupByChanged = hasGroupByChanged();

        prevFiltersRef.current = [...activeFilters];
        prevGroupByRef.current = groupBy;

        const hasDateRange = activeFilters.some((filter) => filter.filterKey === 'dateRange');

        if (!hasDateRange) {
            setLoading(false);
            setTableData([]);
            return;
        }

        setPage(0);
        setHasMore(true);
        fetchTableData(false, 0);
    }, [activeFilters, groupBy]);

    // Effect to handle pagination
    useEffect(() => {
        if (page > 0) {
            fetchTableData(true);
        }
    }, [page]);

    useEffect(() => {
        if (activeFilters.some((filter) => filter.filterKey === 'dateRange')) {
            setPage(0);
            setHasMore(true);
            fetchTableData(false);
        }
    }, [summarizePackout]);

    return (
        <div className="relative w-full inventory-history-v2">
            {/* Main scrollable content - hide its horizontal scrollbar with CSS */}
            <div ref={scrollContainerRef} className="hide-scrollbar inventory-table-container">
                <div className="inventory-table-content">
                    {loading ? (
                        <div className="flex justify-center items-center h-64">
                            <div className="spinner"></div>
                        </div>
                    ) : tableData.length === 0 ? (
                        <div className="flex justify-center items-center h-64 text-gray-500 text-lg">
                            {activeFilters.some((filter) => filter.filterKey === 'dateRange')
                                ? 'No data available for selected filter criteria'
                                : 'Select a date range to view inventory history.'}
                        </div>
                    ) : (
                        <>
                            <CollapsibleTable
                                rows={tableData}
                                columns={getColumns()}
                                isLoading={loading}
                                expandedFilters={activeFilters}
                                setNotification={setNotification}
                                filtersToApiParams={filtersToApiParams}
                                facilityId={facilityId}
                                summarizePackout={summarizePackout}
                                fetchAdjustmentReasons={fetchAdjustmentReasons}
                                groupBy={groupBy}
                            />
                            {/* Infinite scroll loader element */}
                            {hasMore && (
                                <div id="infinite-scroll-loader" className="flex justify-center items-center py-4">
                                    {isLoadingMore && <div className="spinner"></div>}
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* Fixed bottom scrollbar that syncs with the table */}
            <div className="sticky bottom-0 left-0 right-0 w-full bg-white shadow-md z-10" style={{ height: '20px' }}>
                <div ref={syncedScrollRef} className="synced-scrollbar">
                    <div className="scrollbar-inner"></div>
                </div>
            </div>
        </div>
    );
};

export { InventoryHistoryTableContainer };
